// src/components/QuestionEditor.js
import React from 'react';
import { ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import '../styles/SurveyForm.css';
import '../styles/common.css';
import LimitedTextarea from './LimitedTextarea';
/**
 * Props:
 * - question: { text: string, type: 'free_text' | 'multiple_choice', options?: string[] }
 * - index: number
 * - onUpdate: (index, updatedQuestion) => void
 * - onDelete: (index) => void
 * - onMove: (index, 'up' | 'down') => void
 * - onTypeChange: (type: string) => void
 * - onAddOption: () => void
 */
export default function QuestionEditor({
  question,
  index,
  onUpdate,
  onDelete,
  onMove,
  onTypeChange,
  onAddOption,
}) {
  const ICON_SIZE = 16;

  const updateText = (e) => {
    onUpdate(index, { ...question, text: e.target.value });
  };

  const handleOptionChange = (optIdx, value) => {
    const nextOpts = [...(question.options || [])];
    nextOpts[optIdx] = value;
    onUpdate(index, { ...question, options: nextOpts });
  };

  const removeOption = (optIdx) => {
    const nextOpts = (question.options || []).filter((_, i) => i !== optIdx);
    onUpdate(index, { ...question, options: nextOpts });
  };

  return (
    <div className="question-editor">
      {/* Question text */}
      <label className="field-label" htmlFor={`q-text-${index}`}>Question</label>
      {/* Question controls: reorder/delete */}
      <div className="question-actions" style={{ display: 'flex', gap: 6, margin: '4px 0 8px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="post-button icon-button"
          aria-label="Move question up"
          title="Move up"
          onClick={() => onMove(index, 'up')}
        >
          <ArrowUp size={ICON_SIZE} />
        </button>
        <button
          type="button"
          className="post-button icon-button"
          aria-label="Move question down"
          title="Move down"
          onClick={() => onMove(index, 'down')}
        >
          <ArrowDown size={ICON_SIZE} />
        </button>
        <button
          type="button"
          className="post-button icon-button danger"
          aria-label="Delete question"
          title="Delete question"
          onClick={() => onDelete(index)}
        >
          <Trash2 size={ICON_SIZE} />
        </button>
      </div>
      <LimitedTextarea
        id={`q-text-${index}`}
        className="survey-textarea"
        rows={2}
        placeholder="Write your question…"
        value={question.text || ''}
        onChange={updateText}
      />

      <div
        className="question-type-row"
        style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      >
        <label className="field-label" htmlFor={`q-type-${index}`} style={{ margin: 0 }}>
          Question Type
        </label>
        <select
          id={`q-type-${index}`}
          value={question.type}
          onChange={(e) => onTypeChange && onTypeChange(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          <option value="free_text">Free text</option>
          <option value="multiple_choice">Multiple choice</option>
        </select>
        {question.type === 'multiple_choice' && (
          <button
            type="button"
            className="post-button"
            onClick={() => onAddOption && onAddOption()}
            title="Add Option"
          >
            + Add Option
          </button>
        )}
      </div>

      {/* Multiple choice options (Add handled by SurveyForm) */}
      {question.type === 'multiple_choice' && (
        <div className="multiple-choice-options">
          {(question.options || []).map((opt, optIdx) => (
            <div key={optIdx} className="mc-option-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input
                type="text"
                className="survey-input"
                placeholder={`Option ${optIdx + 1}`}
                value={opt}
                onChange={(e) => handleOptionChange(optIdx, e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="post-button icon-button danger"
                aria-label={`Remove option ${optIdx + 1}`}
                title="Remove option"
                onClick={() => removeOption(optIdx)}
              >
                <Trash2 size={ICON_SIZE} />
              </button>
            </div>
          ))}

          {/* Multi-select toggle */}
          <div className="survey-row-horizontal" style={{ marginTop: 6, alignItems: 'center', gap: 8 }}>
            <input
              id={`q-${index}-allow-multi`}
              type="checkbox"
              checked={!!question.allowMultiple}
              onChange={(e) => onUpdate(index, { ...question, allowMultiple: e.target.checked })}
            />
            <label htmlFor={`q-${index}-allow-multi`} title="If checked, responders can select more than one option">
              Allow multiple selections
            </label>
          </div>
        </div>
      )}

    </div>
  );
}
